import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import {
  adminPath,
  membersPath,
  userGroupListPath,
} from 'features/admin/components/admin-paths';
import {
  DataTable,
  DataTableListOptions,
  DataTableSortingRule,
} from 'features/common/components/DataTable';
import { SelectBar } from 'features/common/components/DataTable/SelectBar';
import {
  getTableOptions,
  transformDataForTable,
  useRowActions,
} from 'features/members/components/table/membersTableHelpers';
import { useMemberGuards } from 'features/members/utils/lib';
import { useGroupData } from 'features/user-group/user-group-details/hooks/useGroupData';
import { UserGroupPlaceholder } from 'features/user-group/user-group-list/components/UserGroupPlaceholder';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useOrgGroupMembersList } from '../api/useOrgGroupMembersList';
import { useGroupMembersSearch } from '../hooks/useGroupMemberSearch';
import { useRemoveMembers } from '../hooks/useRemoveMembers';
import { RemoveMemberConfirmationModal } from './RemoveMemberConfirmationModal';
import { SearchGroupMemberInput } from './SearchGroupMemberInput';

const PAGE_SIZE = 10;
const TableDiv = styled.div`
  table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;
export const GroupMembersTab = () => {
  const history = useHistory();
  const { id: groupId, page: pageFromUrl } = useParams<{
    id: string;
    page?: string;
  }>();
  const currentPage = pageFromUrl ? parseInt(pageFromUrl) : 1;
  const pageSize = PAGE_SIZE;
  const [sortBy, setSortBy] = useState<DataTableSortingRule[]>([
    { id: 'givenName', desc: false },
  ]);
  const [selectedRowIds, setSelectedRowIds] = useState<Record<string, boolean>>(
    {},
  );
  const clearSelectionRef = useRef<(() => void) | null>(null);

  const sortField = sortBy[0]?.id;
  const sortDesc = sortBy[0]?.desc ?? false;
  const {
    debouncedSearch,
    searchFilter,
    onSearchChange,
    clearSearch,
    isValid,
  } = useGroupMembersSearch();

  const {
    data: orgGroupMembers,
    isLoading,
    isRefetching,
  } = useOrgGroupMembersList(groupId, {
    page: currentPage,
    pageSize: PAGE_SIZE,
    sortField,
    sortDesc: sortField ? sortDesc : undefined,
    query: debouncedSearch && debouncedSearch ? debouncedSearch : undefined,
  });

  const totalRecords = orgGroupMembers?.totalRecords ?? 0;

  const displayData = useMemo(
    () => transformDataForTable(orgGroupMembers?.data) ?? [],
    [orgGroupMembers?.data],
  );
  const selectedMemberIds = useMemo(
    () => Object.keys(selectedRowIds),
    [selectedRowIds],
  );

  const { name: groupName } = useGroupData(groupId);
  const pagination = totalRecords < pageSize ? 'hidden' : 'complex';

  const {
    isOpen,
    membersToRemove,
    handleOpen,
    handleClose,
    handleConfirm,
    isLoading: isDeleting,
  } = useRemoveMembers({
    groupId,
    onSuccess: () => clearSelectionRef.current?.(),
  });

  const { allowMemberAccess } = useMemberGuards();

  const rowActions = useRowActions(currentPage, handleOpen);

  const options = useMemo(
    () =>
      getTableOptions({
        page: currentPage,
        allowMemberAccess,
        hideColumn: { access: true, teams: true },
        rowActions,
      }),
    [currentPage, allowMemberAccess, rowActions],
  );

  const onClickRow = useCallback(
    (row: { id: string }) => {
      history.push({
        pathname: `${adminPath}${membersPath}/page/${currentPage}/details/${row.id}`,
        search: history.location.search,
        state: {
          returnTo: `${adminPath}${userGroupListPath}/${groupId}/members/page/${currentPage}${history.location.search}`,
        },
      });
    },
    [history, currentPage, groupId],
  );

  const onChange = useCallback(
    (options: DataTableListOptions) => {
      if (options.sortBy) {
        setSortBy(options.sortBy);
      }

      history.push({
        pathname: `${adminPath}${userGroupListPath}/${groupId}/members/page/${options.page}`,
      });
    },
    [history, groupId],
  );

  useEffect(() => {
    if (
      !isLoading &&
      !isRefetching &&
      displayData.length === 0 &&
      totalRecords > 0 &&
      currentPage > 1
    ) {
      const lastAvailablePage = Math.ceil(displayData.length / pageSize);
      onChange({ page: lastAvailablePage, sortBy });
    }
  }, [
    displayData.length,
    currentPage,
    isLoading,
    isRefetching,
    pageSize,
    onChange,
    sortBy,
    totalRecords,
  ]);

  return (
    <Flex direction='column'>
      <SearchGroupMemberInput
        value={searchFilter}
        onChange={onSearchChange}
        onClear={clearSearch}
        isValid={isValid}
      />
      {!isLoading && !isRefetching && displayData.length === 0 ? (
        <UserGroupPlaceholder
          description={
            debouncedSearch
              ? 'Change your search parameters and try again.'
              : 'Invite users to this group to streamline permission management and collaboration across your organization.'
          }
          title={debouncedSearch ? 'No users found' : 'Invite your first user'}
          src={
            debouncedSearch
              ? 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-magnify-close-neutral'
              : 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-account-group-neutral'
          }
        />
      ) : (
        <TableDiv>
          <DataTable
            columns={options.columns}
            selectable
            hideSelectAll
            maxSelection={5}
            //@ts-ignore
            rowActions={options.rowActions}
            data={displayData}
            page={currentPage}
            pageSize={pageSize}
            isLoading={isLoading || isRefetching}
            sortBy={sortBy}
            autoSort={false}
            totalItems={totalRecords}
            getRowId={(row) => row.id}
            onClickRow={onClickRow}
            onSelectedRowIdsChange={setSelectedRowIds}
            theaderProps={{ 'data-behavior-analytics-id': 'members sorting' }}
            paginationProps={{
              'data-behavior-analytics-feature': ' members pagination',
            }}
            onChange={onChange}
            pagination={pagination}
          >
            {({ clearSelection }) => {
              clearSelectionRef.current = clearSelection;

              return (
                <Box
                  position='fixed'
                  bottom='35px'
                  left='50%'
                  transform='translateX(-50%)'
                  w='full'
                  maxW='379px'
                  h='64px'
                  zIndex={10}
                >
                  <SelectBar
                    selectedCount={selectedMemberIds.length}
                    onDeselectAll={clearSelection}
                    onDelete={() => handleOpen(selectedMemberIds)}
                  />
                </Box>
              );
            }}
          </DataTable>
        </TableDiv>
      )}
      {isOpen && (
        <RemoveMemberConfirmationModal
          groupName={groupName}
          isOpen={isOpen}
          membersId={membersToRemove}
          onSuccess={handleConfirm}
          onClose={handleClose}
          isLoading={isDeleting}
        />
      )}
    </Flex>
  );
};
